import React, { useState } from 'react';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import TabPanel from './TabPanel';
import ResultsTable from './ResultsTable';

interface ClassificationResult {
  keyword: string;
  topic: string;
  subtopic: string;
  relevance: number;
  confidence: number;
  location: boolean;
}

interface ClassificationResultsProps {
  isClassifying: boolean;
  classificationResults: {
    relevant: ClassificationResult[];
    undecided: ClassificationResult[];
    outlier: ClassificationResult[];
    statistics: {
      total: number;
      relevant: number;
      undecided: number;
      outlier: number;
    };
  } | null;
}

const ClassificationResults: React.FC<ClassificationResultsProps> = ({
  isClassifying,
  classificationResults
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handler for tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset pagination when changing tabs
  };

  // Handlers for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isClassifying) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Classifying keywords...
        </Typography>
      </Box>
    );
  }

  if (!classificationResults) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <Typography variant="body1" color="text.secondary">
          Select files and start classification to see results
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="classification result tabs">
          <Tab label={`Relevant (${classificationResults.relevant.length})`} />
          <Tab label={`Undecided (${classificationResults.undecided.length})`} />
          <Tab label={`Outlier (${classificationResults.outlier.length})`} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <ResultsTable
          results={classificationResults.relevant}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ResultsTable
          results={classificationResults.undecided}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <ResultsTable
          results={classificationResults.outlier}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TabPanel>
    </Box>
  );
};

export default ClassificationResults;
